import { api } from "api/api";
import { AxiosError } from "axios";
import CentroCusto from "model/administracao/cadastro/centrocusto/CentroCusto";
import Usuario from "model/administracao/cadastro/Usuario";
import Page from "model/page/Page";
import Pageable from "model/page/Pageable";
import Viagem from "model/viagem/Viagem";
import ViagemTipoDespesa from "model/viagem/ViagemTipoDespesa";
import { DataTableFilterMeta, DataTableOperatorFilterMetaData } from "primereact/datatable";
import { BaseServiceCadastros } from "service/BaseServiceCadastros";
import { getFilters } from "utils/filterUtils";

class ViagemService implements BaseServiceCadastros<Viagem> {
    
    pesquisar(page: Pageable, filters?: DataTableFilterMeta | null) {
        return new Promise<Page>((resolve, reject): void => {
            let url = `/viagem/pesquisar`;
            url += getFilters(filters);

            const params = {
                page: page.page,
                size: page.size,
                sort: page.sort,
            };

            api
                .get(url, { params })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    pesquisarUsuario(page: Pageable, filters?: DataTableFilterMeta | null) {
        return new Promise<Page>((resolve, reject): void => {
            let url = `/viagem/pesquisarUsuario`;
            url += getFilters(filters);

            const params = {
                page: page.page,
                size: page.size,
                sort: page.sort,
            };

            api
                .get(url, { params })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    consultar(id: number | string) {
        return new Promise<Viagem>((resolve, reject): void => {
            api
                .get(`/viagem/consultar/${id}`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    salvar(viagem: Viagem) {
        return new Promise<Viagem>((resolve, reject): void => {
            api
                .post(`/viagem/salvar`, JSON.stringify(viagem))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    salvarOutroUsuario(viagem: Viagem) {
        return new Promise<Viagem>((resolve, reject): void => {
            api
                .post(`/viagem/salvarOutroUsuario`, JSON.stringify(viagem))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    salvarEnviarAprovacao(viagem: Viagem) {
        return new Promise<Viagem>((resolve, reject): void => {
            api
                .post(`/viagem/salvarEnviarAprovacao`, JSON.stringify(viagem))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    cancelar(viagem: Viagem) {
        return new Promise<Viagem>((resolve, reject): void => {
            api
                .post(`/viagem/cancelar`, JSON.stringify(viagem))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    finalizar(viagem: Viagem) {
        return new Promise<Viagem>((resolve, reject): void => {
            api
                .post(`/viagem/finalizar`, JSON.stringify(viagem))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    reabrir(id: number) {
        return new Promise((resolve, reject): void => {
            api
                .put(`/viagem/reabrir/${id}`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    incluir(viagem: Viagem) {
        return new Promise<Viagem>((resolve, reject): void => {
            api
                .post(`/viagem/incluir`, JSON.stringify(viagem))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    editar(viagem: Viagem) {
        return new Promise<Viagem>((resolve, reject): void => {
            api
                .put(`/viagem/editar`, JSON.stringify(viagem))
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    excluir(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .delete(`/viagem/excluir/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getUsuariosDisponiveis() {
        return new Promise<Usuario[]>((resolve, reject): void => {
            api
                .get(`/viagem/usuariosDisponiveis`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getCentroCustoDisponiveis() {
        return new Promise<CentroCusto[]>((resolve, reject): void => {
            api
                .get(`/viagem/centroscusto`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getTiposDespesa() {
        return new Promise<ViagemTipoDespesa[]>((resolve, reject): void => {
            api
                .get(`/viagem/tiposDespesa`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getRelatorioListagem(filter?: DataTableFilterMeta | null) {
        return new Promise<string>((resolve, reject): void => {
            let url = `/viagem/report/listagem`;
            if (filter) {
                url += getFilters(filter)
            }
            api
                .get(url, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getRelatorioViagemSolicitacao(id: number) {
        return new Promise<string>((resolve, reject): void => {
            api
                .get(`/viagem/report/viagemSolicitacao/${id}`, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    getSituacaoFilter() {
        return [
            { label: 'Aberta', value: '1' },
            { label: 'Enviada para aprovação', value: '2' },
            { label: 'Aprovada', value: '3' },
            { label: 'Rejeitada', value: '4' },
            { label: 'Cancelada', value: '5' },
            { label: 'Finalizada', value: '6' },
            { label: 'Apagada', value: '7' },
        ]
    }

    getSituacao(situacao: string): string {

        if (situacao === 'ABERTA') {
            return 'Aberta';
        } else if (situacao === 'ENVIADA_APROVACAO') {
            return 'Enviada para aprovação';
        } else if (situacao === 'APROVADA') {
            return 'Aprovada';
        } else if (situacao === 'REJEITADA') {
            return 'Rejeitada';
        } else if (situacao === 'CANCELADA') {
            return 'Cancelada';
        } else if (situacao === 'FINALIZADA') {
            return 'Finalizada';
        } else if (situacao === "APAGADA") {
            return 'Apagada';
        }
        return '';
    }

    getTipoLocomocaoFilter() {
        return [
            { label: 'Carro Empresa', value: '1' },
            { label: 'Carro Próprio', value: '2' },
            { label: 'Avião Empresa', value: '3' },
            { label: 'Avião Carreira', value: '4' },
            { label: 'Ônibus', value: '5' },
            { label: 'Outros', value: '6' }
        ]
    }

    getTiposLocomocao() {
        return [
            { label: 'Carro Empresa', value: 'CARRO_EMPRESA' },
            { label: "Carro Próprio", value: "CARRO_PROPRIO" },
            { label: "Avião Empresa", value: "AVIAO_EMPRESA" },
            { label: "Avião Carreira", value: "AVIAO_CARREIRA" },
            { label: "Ônibus", value: "ONIBUS" },
            { label: "Outros", value: "OUTROS" },
        ]
    }

    getTipoLocomocao(tipo: string): string {

        if (tipo === 'CARRO_EMPRESA') {
            return 'Carro Empresa';
        } else if (tipo === 'CARRO_PROPRIO') {
            return 'Carro Próprio';
        } else if (tipo === 'AVIAO_EMPRESA') {
            return 'Avião Empresa';
        } else if (tipo === 'AVIAO_CARREIRA') {
            return 'Avião Carreira';
        } else if (tipo === 'ONIBUS') {
            return 'Ônibus';
        } else if (tipo === 'OUTROS') {
            return 'Outros';
        }
        return '';
    }

}

export default ViagemService;